import type React from "react";
import { useState } from "react";

import { AlertCircle, FileBarChart2, FolderKey, Plus, RefreshCcw } from "lucide-react";
import { Link } from "react-router-dom";

import { Can } from "@/features/rbac/components/Can";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Pagination, UsersFilters, UsersTable, useUsersPage } from "@/features/users";
import type { UserAction } from "@/features/users/components/UserActionsDialog";
import { UserActionsDialog } from "@/features/users/components/UserActionsDialog";
import { UserCard } from "@/features/users/components/UserCard";
import { UserEditDialog } from "@/features/users/components/UserEditDialog";
import { UserImagingsDialog } from "@/features/users/components/UserImagingsDialog";
import { UsersBottomSheet } from "@/features/users/components/UsersBottomSheet";
import { PermissionGroupsDialog } from "@/features/users/components/PermissionGroupsDialog";
import { useInvalidateUserQueries } from "@/features/users/hooks/useUsers";
import type { User } from "@/features/users/schemas/user";
import { BREAKPOINTS, useMediaQuery } from "@/shared/hooks/useMediaQuery";

export default function Users(): React.JSX.Element {
  const {
    filters,
    setFilters,
    users,
    isLoading,
    isFetching,
    error,
    currentUser,
    searchTerm,
    setSearchTerm,
    pagination,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    refetch,
    setRefreshKey,
  } = useUsersPage();

  const { invalidateAll } = useInvalidateUserQueries();

  const isMobile = useMediaQuery(BREAKPOINTS.mobile);

  const { can } = usePermissions();
  const canAccessServersReport = can("servers:report_view");

  // Estado del modal de edición
  const [editUser, setEditUser] = useState<User | null>(null);

  // Estado del modal de acciones
  const [dialogUser, setDialogUser] = useState<User | null>(null);
  const [dialogAction, setDialogAction] = useState<UserAction | null>(null);

  // Estado del dialog de imagenología
  const [imagingsUser, setImagingsUser] = useState<User | null>(null);

  // Gestión de grupos de permisos API
  const [showPermissionGroups, setShowPermissionGroups] = useState(false);

  const handleRefresh = async () => {
    setRefreshKey(Date.now().toString());
    await invalidateAll();
    refetch();
  };

  const openAction = (user: User, action: UserAction) => {
    setDialogUser(user);
    setDialogAction(action);
  };

  const closeDialog = () => {
    setDialogUser(null);
    setDialogAction(null);
  };

  const handleEdit = (user: User) => {
    setEditUser(user);
  };

  const handleView = (user: User) => {
    // TODO: Navegar a detalle de usuario
    console.log("Ver usuario:", user);
  };

  const handleViewImagings = (user: User) => {
    setImagingsUser(user);
  };

  const handleNewUser = () => {
    // TODO: Abrir modal de creación
    console.log("Crear nuevo usuario");
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Usuarios
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {pagination
              ? `${pagination.total.toLocaleString()} usuarios en total`
              : "Gestiona los usuarios del sistema"}
          </p>
        </div>

        {/* Desktop: inline button + Mobile: bottom sheet filter trigger */}
        <div className="flex items-center gap-2">
          {isMobile ? (
            <div className="flex gap-2 w-full">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading || isFetching}
                className="flex-1 h-9"
              >
                <RefreshCcw
                  className={`h-4 w-4 mr-2 ${isLoading || isFetching ? "animate-spin" : ""}`}
                />
                Recargar
              </Button>
              {canAccessServersReport && (
                <Button variant="outline" size="sm" className="h-9" asChild>
                  <Link to="/servers/report" title="Reporte de software">
                    <FileBarChart2 className="h-4 w-4" />
                  </Link>
                </Button>
              )}
              <UsersBottomSheet
                filters={filters}
                onFiltersChange={setFilters}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isLoading={isLoading}
              />
              <Can permission="api_catalog:groups_edit">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPermissionGroups(true)}
                  className="h-9"
                  title="Grupos de permisos API"
                >
                  <FolderKey className="h-4 w-4" />
                </Button>
              </Can>
            </div>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading || isFetching}
                className="h-9 shrink-0"
              >
                <RefreshCcw
                  className={`h-4 w-4 mr-2 ${isLoading || isFetching ? "animate-spin" : ""}`}
                />
                Recargar
              </Button>
              {canAccessServersReport && (
                <Button variant="outline" size="sm" className="h-9" asChild>
                  <Link to="/servers/report">
                    <FileBarChart2 className="h-4 w-4 mr-2" />
                    Reporte software
                  </Link>
                </Button>
              )}
              <Can permission="api_catalog:groups_edit">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => setShowPermissionGroups(true)}
                >
                  <FolderKey className="h-4 w-4 mr-2" />
                  Grupos API
                </Button>
              </Can>
              <Can permission="users:create">
                <Button onClick={handleNewUser} size="sm" className="h-9">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Usuario
                </Button>
              </Can>
            </>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
          <p className="text-sm text-red-700">Error al cargar usuarios: {error}</p>
        </div>
      )}

      {/* Filtros — solo en desktop */}
      {!isMobile && (
        <UsersFilters
          filters={filters}
          onFiltersChange={setFilters}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          isLoading={isLoading}
        />
      )}

      {/* Contenido — cards en móvil, tabla en desktop */}
      {isMobile ? (
        <div className="space-y-3">
          {isLoading ? (
            // Skeleton cards
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse"
              >
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-full bg-gray-200 dark:bg-gray-700" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                    <div className="flex gap-2 mt-2">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-14" />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-base font-medium mb-1">No hay usuarios</h3>
              <p className="text-sm text-muted-foreground">
                No se encontraron usuarios con los filtros aplicados.
              </p>
            </div>
          ) : (
            users.map((user) => (
              <UserCard
                key={user.uid}
                user={user}
                currentUser={currentUser}
                onEdit={handleEdit}
                onView={handleView}
                onAction={openAction}
                onViewImagings={handleViewImagings}
              />
            ))
          )}
        </div>
      ) : (
        <UsersTable
          users={users}
          isLoading={isLoading}
          onEdit={handleEdit}
          onView={handleView}
          onAction={openAction}
          onViewImagings={handleViewImagings}
          currentUser={currentUser}
        />
      )}

      {/* Paginación */}
      {pagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          pageSize={pageSize}
          totalItems={pagination.total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      {/* FAB para nuevo usuario — solo móvil */}
      {isMobile && (
        <Can permission="users:create">
          <button
            onClick={handleNewUser}
            className="fixed right-4 bottom-20 z-30 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-strong flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all touch-action-manipulation"
            aria-label="Nuevo usuario"
          >
            <Plus className="h-6 w-6" />
          </button>
        </Can>
      )}

      {/* Modal de acciones */}
      <UserActionsDialog
        user={dialogUser}
        action={dialogAction}
        open={!!dialogUser && !!dialogAction}
        onClose={closeDialog}
      />

      {/* Modal de edición de perfil / email */}
      <UserEditDialog
        user={editUser}
        open={!!editUser}
        onClose={() => setEditUser(null)}
      />

      {/* Dialog de imagenología */}
      <UserImagingsDialog
        user={imagingsUser}
        open={!!imagingsUser}
        onClose={() => setImagingsUser(null)}
      />

      <PermissionGroupsDialog
        open={showPermissionGroups}
        onClose={() => setShowPermissionGroups(false)}
      />
    </div>
  );
}
